import React, { useState, useEffect } from 'react';
import * as api from '../../../api/contentStudioV2';
import { useProgressWrapper } from '../../../utils/withProgress';

const Step10Storyboard = ({ sessionId, token, sessionData, onBack, setError, setLoading }) => {
  const [storyboardData, setStoryboardData] = useState(null);
  const [generatingStoryboard, setGeneratingStoryboard] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  // Pre-fill project name from sessionData if exists (for edit/resubmit mode)
  const [projectName, setProjectName] = useState(sessionData?.submission?.project_name || sessionData?.project_name || '');
  const [projectNameError, setProjectNameError] = useState('');  // Validation error
  const withProgress = useProgressWrapper();

  // Update project name if sessionData changes (e.g., when loading edit session)
  useEffect(() => {
    const existingProjectName = sessionData?.submission?.project_name || sessionData?.project_name;
    if (existingProjectName && !projectName) {
      setProjectName(existingProjectName);
    }
  }, [sessionData, projectName]);

  // Check if storyboard already generated
  useEffect(() => {
    if (sessionData?.storyboard_beats && sessionData.storyboard_beats.length > 0) {
      setStoryboardData({
        storyboard_beats: sessionData.storyboard_beats,
        hook_validation: sessionData.hook_validation,
        pacing_validation: sessionData.pacing_validation,
        total_duration_seconds: sessionData.platform_context?.normalized_seconds || 60
      });
    }
  }, [sessionData]);

  const handleGenerateStoryboard = async () => {
    setGeneratingStoryboard(true);
    try {
      const result = await withProgress(
        {
          label: 'Creating storyboard',
          phases: [
            'Planning scenes',
            'Generating visuals',
            'Adding transitions',
            'Validating hook',
            'Finalizing'
          ],
          canCancel: false,
          timeoutMs: 30000,
          meta: {
            session_id: sessionId,
            feature: 'content_studio_step10_storyboard'
          }
        },
        async (signal) => api.generateStoryboard(token, sessionId)
      );
      setStoryboardData(result);
    } catch (err) {
      setError('Failed to generate storyboard: ' + err.message);
    } finally {
      setGeneratingStoryboard(false);
    }
  };

  const handleSubmitForApproval = async () => {
    // Validate project name
    if (!projectName.trim()) {
      setProjectNameError('Sila masukkan nama projek');
      return;
    }
    setProjectNameError('');
    
    try {
      const result = await withProgress(
        {
          label: 'Submitting for approval',
          phases: ['Validating storyboard', 'Submitting'],
          canCancel: false,
          timeoutMs: 10000,
          meta: { session_id: sessionId }
        },
        async (signal) => api.submitStoryboardForApproval(token, sessionId, projectName.trim(), 'Ready for supervisor review')
      );
      
      // Clear localStorage for this session after successful submission
      // This ensures a fresh start when user returns to Content Studio
      localStorage.removeItem('cs_v2_session_id');
      localStorage.removeItem(`cs_v2_${sessionId}_step`);
      localStorage.removeItem(`cs_v2_${sessionId}_data`);
      console.log('[Step10] Cleared localStorage after successful submission');
      
      setSubmissionData(result);
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit for approval: ' + err.message);
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h2 style={{ fontSize: '20px', color: '#333', marginBottom: '8px' }}>Submitted for Approval!</h2>
        <p style={{ fontSize: '16px', color: '#0e67a2', fontWeight: 600, marginBottom: '8px' }}>
          {projectName}
        </p>
        <p style={{ fontSize: '12px', color: '#999', marginBottom: '16px' }}>
          ID: {submissionData?.submission_id}
        </p>
        
        {/* Summary Card */}
        <div style={{ padding: '16px', background: '#E8F4FF', borderRadius: '12px', marginBottom: '16px', textAlign: 'left' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#0e67a2', marginBottom: '12px' }}>
            📋 Submission Summary
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#333' }}>
            <div>
              <span style={{ color: '#666' }}>Platform: </span>
              <strong>{submissionData?.summary?.platform?.join(', ') || 'N/A'}</strong>
            </div>
            <div>
              <span style={{ color: '#666' }}>Duration: </span>
              <strong>{submissionData?.summary?.duration || 0}s</strong>
            </div>
            <div>
              <span style={{ color: '#666' }}>Format: </span>
              <strong>{submissionData?.summary?.format || 'N/A'}</strong>
            </div>
            <div>
              <span style={{ color: '#666' }}>Storyboard Beats: </span>
              <strong>{submissionData?.storyboard_summary?.total_beats || 0}</strong>
            </div>
            <div>
              <span style={{ color: '#666' }}>Hook Compliant: </span>
              <strong style={{ color: submissionData?.summary?.hook_compliant ? '#2E7D32' : '#D32F2F' }}>
                {submissionData?.summary?.hook_compliant ? '✓ Yes' : '✗ No'}
              </strong>
            </div>
            <div>
              <span style={{ color: '#666' }}>Pacing Compliant: </span>
              <strong style={{ color: submissionData?.summary?.pacing_compliant ? '#2E7D32' : '#D32F2F' }}>
                {submissionData?.summary?.pacing_compliant ? '✓ Yes' : '✗ No'}
              </strong>
            </div>
          </div>
        </div>

        <div style={{ padding: '12px', background: '#FFF4E6', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', color: '#F7941E' }}>
            🔔 Supervisor has been notified. You will receive a notification once reviewed.
          </div>
        </div>

        <button 
          onClick={() => window.location.href = '/dashboard'}
          style={{ 
            width: '100%', 
            padding: '14px', 
            background: '#0e67a2', 
            border: 'none', 
            borderRadius: '12px', 
            color: '#fff', 
            fontSize: '15px', 
            fontWeight: 600, 
            cursor: 'pointer' 
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // Loading / Initial State
  if (!storyboardData && !generatingStoryboard) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
        <h2 style={{ fontSize: '20px', color: '#333', marginBottom: '8px' }}>
          🎬 Step 10: Storyboard & Supervisor Approval
        </h2>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Generate a storyboard with scene-by-scene breakdown following Format Intelligence Pack rules.
        </p>

        {/* Creator Intent Card */}
        {sessionData?.creative_intent && (
          <div style={{ padding: '16px', background: '#F3E5F5', borderRadius: '12px', marginBottom: '20px', border: '1px solid #CE93D8' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#7B1FA2', marginBottom: '12px' }}>
              ✨ Creator Intent (from Step 6)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '13px' }}>
              <div>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Style</div>
                <div style={{ color: '#333', fontWeight: 600 }}>{sessionData.creative_intent.style}</div>
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Visual Mode</div>
                <div style={{ color: '#333', fontWeight: 600 }}>{sessionData.creative_intent.visual_mode}</div>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Target Emotions</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {sessionData.creative_intent.emotions?.map((emotion, idx) => (
                    <span key={`item-${idx}`} style={{ padding: '4px 10px', background: '#CE93D8', color: '#fff', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                      {emotion}
                    </span>
                  ))}
                </div>
              </div>
              {sessionData.creative_intent.preferred_format && (
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Preferred Format</div>
                  <div style={{ color: '#333', fontWeight: 600 }}>{sessionData.creative_intent.preferred_format}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Platform Context Card */}
        {sessionData?.platform_context && (
          <div style={{ padding: '16px', background: '#E8F4FF', borderRadius: '12px', marginBottom: '20px', border: '1px solid #0e67a2' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#0e67a2', marginBottom: '12px' }}>
              📱 Platform Context (from Step 0)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '13px' }}>
              <div>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Platform(s)</div>
                <div style={{ color: '#333', fontWeight: 600 }}>{sessionData.platform_context.channels?.join(', ') || 'N/A'}</div>
              </div>
              <div>
                <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Duration</div>
                <div style={{ color: '#333', fontWeight: 600 }}>{sessionData.platform_context.normalized_seconds || 0}s</div>
              </div>
            </div>
          </div>
        )}

        {/* Format Card */}
        {sessionData?.selected_format && (
          <div style={{ padding: '16px', background: '#FFF4E6', borderRadius: '12px', marginBottom: '20px', border: '1px solid #F7941E' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#F7941E', marginBottom: '8px' }}>
              🎭 Selected Format (from Step 9)
            </div>
            <div style={{ color: '#333', fontSize: '13px', fontWeight: 600 }}>{sessionData.selected_format.format_name}</div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={handleGenerateStoryboard}
          disabled={generatingStoryboard}
          style={{
            width: '100%',
            padding: '14px',
            background: generatingStoryboard ? '#ccc' : '#0e67a2',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: 600,
            cursor: generatingStoryboard ? 'not-allowed' : 'pointer',
            marginBottom: '12px'
          }}
        >
          {generatingStoryboard ? '⏳ Generating Storyboard...' : '🎬 Generate Storyboard'}
        </button>

        <button
          onClick={onBack}
          style={{
            width: '100%',
            padding: '14px',
            background: '#fff',
            border: '1px solid #E0E0E0',
            borderRadius: '12px',
            color: '#333',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          ← Back to Format Selection
        </button>
      </div>
    );
  }

  // Storyboard Display
  if (storyboardData) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
        <h2 style={{ fontSize: '20px', color: '#333', marginBottom: '8px' }}>
          🎬 Storyboard & Supervisor Approval
        </h2>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Review the generated storyboard and submit for supervisor approval.
        </p>

        {/* Compliance Status */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', background: storyboardData.hook_validation?.compliant ? '#E8F5E9' : '#FFEBEE', borderRadius: '8px', border: `1px solid ${storyboardData.hook_validation?.compliant ? '#2E7D32' : '#D32F2F'}` }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Hook Compliance</div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: storyboardData.hook_validation?.compliant ? '#2E7D32' : '#D32F2F' }}>
              {storyboardData.hook_validation?.compliant ? '✓ Pass' : '✗ Fail'}
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              {storyboardData.hook_validation?.notes || 'N/A'}
            </div>
          </div>
          <div style={{ padding: '12px', background: storyboardData.pacing_validation?.compliant ? '#E8F5E9' : '#FFEBEE', borderRadius: '8px', border: `1px solid ${storyboardData.pacing_validation?.compliant ? '#2E7D32' : '#D32F2F'}` }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Pacing Compliance</div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: storyboardData.pacing_validation?.compliant ? '#2E7D32' : '#D32F2F' }}>
              {storyboardData.pacing_validation?.compliant ? '✓ Pass' : '✗ Fail'}
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              Avg: {storyboardData.pacing_validation?.avg_beat_duration?.toFixed(1) || 'N/A'}s per beat
            </div>
          </div>
        </div>

        {/* Storyboard Beats */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>
            📋 Scene Breakdown ({storyboardData.storyboard_beats?.length || 0} beats)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {storyboardData.storyboard_beats?.map((beat, idx) => (
              <div key={`item-${idx}`} style={{ padding: '14px', background: '#FAFAFA', borderRadius: '8px', border: '1px solid #E0E0E0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ padding: '6px 12px', background: '#0e67a2', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                      Beat {beat.beat_number}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{beat.timing}</div>
                    <div style={{ padding: '4px 8px', background: getSceneTypeColor(beat.scene_type), color: '#fff', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                      {beat.scene_type}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{beat.duration_seconds}s</div>
                </div>
                <div style={{ fontSize: '13px', color: '#333', marginBottom: '6px', fontWeight: 600 }}>
                  {beat.description}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  <strong>Visual:</strong> {beat.visual_notes}
                </div>
                {beat.audio_notes && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <strong>Audio:</strong> {beat.audio_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        
        {/* Project Name Input - Required before submission */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
            📝 Nama Projek <span style={{ color: '#E53935' }}>*</span>
          </label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
              if (e.target.value.trim()) setProjectNameError('');
            }}
            placeholder="Contoh: Kempen Raya 2026, Promo 11.11, Launch Produk Baru"
            style={{
              width: '100%',
              padding: '14px',
              border: projectNameError ? '2px solid #E53935' : '2px solid #E0E0E0',
              borderRadius: '12px',
              fontSize: '15px',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#0e67a2'}
            onBlur={(e) => e.target.style.borderColor = projectNameError ? '#E53935' : '#E0E0E0'}
          />
          {projectNameError && (
            <p style={{ color: '#E53935', fontSize: '12px', marginTop: '4px' }}>
              ⚠️ {projectNameError}
            </p>
          )}
          <p style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
            Nama ini akan dipaparkan kepada supervisor dan memudahkan anda mengenal pasti projek.
          </p>
        </div>
        
        <button
          onClick={handleSubmitForApproval}
          disabled={!projectName.trim()}
          style={{
            width: '100%',
            padding: '14px',
            background: projectName.trim() ? '#2E7D32' : '#BDBDBD',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: 600,
            cursor: projectName.trim() ? 'pointer' : 'not-allowed',
            marginBottom: '12px',
            transition: 'background 0.2s'
          }}
        >
          ✓ Submit for Supervisor Approval
        </button>

        <button
          onClick={onBack}
          style={{
            width: '100%',
            padding: '14px',
            background: '#fff',
            border: '1px solid #E0E0E0',
            borderRadius: '12px',
            color: '#333',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  return null;
};

// Helper function for scene type colors
const getSceneTypeColor = (sceneType) => {
  const colors = {
    'Hook': '#D32F2F',
    'Body': '#0e67a2',
    'Transition': '#F7941E',
    'CTA': '#2E7D32',
    'Outro': '#7B1FA2'
  };
  return colors[sceneType] || '#666';
};

export default Step10Storyboard;
